import type { RefCallbackWithCleanup } from 'react';

/**
 * A factory function which creates a ref callback that merges multiple ref callbacks.
 * The callbacks are called in order, and the cleanup functions execute in the same order.
 * @param callbacks - The callbacks to call when the ref is set.
 * @returns A ref callback that can be attached to a React element.
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * const mergedRef = useMemo(
 *  () => refMerge<HTMLDivElement>(onClickedOutside(() => closeModal()), refAssign(ref)),
 *  [isOpen, onToggle]
 * );
 *
 * return <div ref={mergedRef}>...</div>;
 */
export function refMerge<T extends Node>(
  ...callbacks: Array<RefCallbackWithCleanup<T>>
): RefCallbackWithCleanup<T> {
  return (node: T) => {
    const cleanups = callbacks.map((callback) => callback(node));
    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  };
}
